/**
 * At most `limit` tasks at once; the rest wait their turn, in order.
 *
 * RxDB's upstream gathers every changed document of a collection, cuts them
 * into `push.batchSize` pieces and sends ALL pieces at once (`Promise.all` in
 * replication-protocol/upstream.js). After a large import that is a few
 * hundred simultaneous requests across the collections. RxDB waits for all of
 * them anyway, so holding them back here changes the timing, not the outcome.
 *
 * `leaseMs` bounds how long one task may hold its slot. A task still running
 * by then gives the slot to the next one and finishes on its own; it is never
 * cancelled. Aborting a push would not stop the server, which would go on
 * writing while the retry sends the same rows again.
 */
export function createLimiter(limit: number, leaseMs?: number) {
    let active = 0;
    const waiting: (() => void)[] = [];

    /**
     * A freed slot passes STRAIGHT to the next waiter, `active` unchanged.
     * Counting it down first would let a caller arriving in between take the
     * slot ahead of the queue and push `active` past the limit.
     */
    function release(): void {
        const next = waiting.shift();
        if (next) {
            next();
        } else {
            active -= 1;
        }
    }

    return async function run<T>(task: () => Promise<T>): Promise<T> {
        if (active >= limit) {
            await new Promise<void>((resolve) => waiting.push(resolve));
        } else {
            active += 1;
        }
        let released = false;
        const free = () => {
            if (!released) {
                released = true;
                release();
            }
        };
        const lease = leaseMs === undefined ? null : setTimeout(free, leaseMs);
        try {
            return await task();
        } finally {
            if (lease) {
                clearTimeout(lease);
            }
            free();
        }
    };
}
